/**
 * appVersion — stamps every PostHog event with the build it came from.
 *
 * WHY THIS EXISTS
 * ---------------
 * Until now nothing in an exception event said WHICH build produced it. That
 * is not a cosmetic gap: the native realtime-socket investigation (issues
 * 019fda8e / 019fcba7 / 019fde7f) could not distinguish "still broken on the
 * current release" from "old APK that never got the fix", because a Capacitor
 * WebView reports `$lib: web` and `$current_url: https://localhost` no matter
 * which APK it is running inside. Two users on two different installs are
 * indistinguishable in error tracking.
 *
 * It also cannot be recovered after the fact — the events are already stored
 * without the property — so the sooner this is registered, the sooner any
 * "is this fixed in the shipped build?" question becomes answerable.
 *
 * WHAT IT SETS
 * ------------
 * Registered as PostHog SUPER PROPERTIES (`posthog.register`), which are
 * attached to every subsequent event from this client, including `$exception`.
 * That is deliberate: §20 restricts new *capture call sites* to
 * ComprehensiveErrorBoundary + main.tsx, and this adds none — it only enriches
 * events those existing sites already send.
 *
 *   app_build_id     web bundle identity, from Vite's __BEHTARI_BUILD_ID__
 *                    define — the SAME per-deploy id that versions sw.js and
 *                    the asset caches. Deliberately reused rather than
 *                    inventing a parallel versioning scheme.
 *   app_platform     'android' | 'ios' | 'web'
 *   app_native       boolean — true inside the Capacitor shell
 *   app_version_name native only, e.g. "1.0.8" (or "1.0.3-debug.183")
 *   app_version_code native only, e.g. 11
 *   app_channel      native only: 'sideload' when versionName carries the
 *                    `-debug.` marker that android-debug-build.yml stamps,
 *                    'play' otherwise. Same discriminator useAppUpdateAvailable
 *                    already relies on (§25b) — not a second scheme.
 *
 * The native read is async (`App.getInfo()` crosses the Capacitor bridge), so
 * the web/build properties are registered SYNCHRONOUSLY first and the native
 * ones are merged in when they arrive. A handful of very early events may
 * carry only the build id; every later event carries the full set. Registering
 * synchronously first is what guarantees an early crash is still attributable
 * to a bundle.
 */
import posthog from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import { isNativeApp } from '@/lib/nativeApp';

/** Matches the `-debug.<run>` suffix stamped only by android-debug-build.yml.
 *  Kept identical to DEBUG_CHANNEL_MARKER in useAppUpdateAvailable.ts. */
const DEBUG_CHANNEL_MARKER = '-debug.';

const BUILD_ID =
  typeof __BEHTARI_BUILD_ID__ !== 'undefined' ? __BEHTARI_BUILD_ID__ : 'dev';

/** Safe no-op unless PostHog actually initialised (dev, or no key). */
function register(props: Record<string, unknown>): void {
  try {
    if (posthog.__loaded) posthog.register(props);
  } catch {
    /* telemetry must never throw into app startup */
  }
}

/**
 * Call once from main.tsx, immediately after posthog.init(). Never throws and
 * never blocks: the synchronous half runs inline, the native half resolves on
 * its own.
 */
export function registerAppVersionTelemetry(): void {
  register({
    app_build_id: BUILD_ID,
    app_platform: Capacitor.getPlatform(),
    app_native: isNativeApp(),
  });

  if (!isNativeApp()) return;

  // Dynamic import so the plugin is never pulled into the web bundle's
  // critical path — mirrors how the other native-only helpers load it.
  void import('@capacitor/app')
    .then(({ App: CapacitorApp }) => CapacitorApp.getInfo())
    .then((info) => {
      const versionName = String(info?.version ?? '');
      register({
        app_version_name: versionName,
        // `build` is Android's versionCode. Keep it numeric so PostHog can
        // filter with >= and a release comparison actually works.
        app_version_code: Number(info?.build) || null,
        app_channel: versionName.includes(DEBUG_CHANNEL_MARKER) ? 'sideload' : 'play',
      });
    })
    .catch(() => {
      // A failed bridge read must not look like a missing build. Say so
      // explicitly rather than leaving the properties absent and ambiguous.
      register({ app_version_name: 'unknown', app_version_code: null });
    });
}
